import React, { useCallback } from 'react';
import {
    TextField,
    Button,
    InputAdornment,
    Stack,
    Box,
    Typography,
    Menu,
    MenuItem,
    Chip,
    Popover,
    Slider,
    FormControl,
    Select
} from '@mui/material';
import {
    Search as SearchIcon,
    KeyboardArrowDown as KeyboardArrowDownIcon,
    Clear as ClearIcon,
    FilterList as FilterListIcon
} from '@mui/icons-material';
import debounce from 'lodash/debounce';

const propertyTypes = [
    { value: 'アパート', label: 'アパート' },
    { value: 'マンション', label: 'マンション' },
    { value: '一戸建て', label: '一戸建て' },
    { value: 'シェアハウス', label: 'シェアハウス' }
];

const roomTypes = [
    { value: '完全個室', label: '完全個室' },
    { value: '個室無し', label: '個室無し' }
];

const moveinTypes = [
    { value: 'immediate', label: '即入居可' },
    { value: 'one_month', label: '1ヶ月後以降' },
    { value: 'three_months', label: '3ヶ月後以降' }
];

const availabilityType = [
    { value: 'flexible', label: '相談可' },
    { value: 'short', label: '短期（半年以内）' },
    { value: 'medium', label: '中期（2年以内）' },
    { value: 'long', label: '長期（2年以上）' }
];

const amenities = [
    { value: 'bath', label: '専用バス・トイレ' },
    { value: 'separate_toilet', label: 'トイレ別' },
    { value: 'washer', label: '洗濯機置き場' },
    { value: 'utilities', label: '光熱費込み' },
    { value: '2nd_floor', label: '2階以上' },
    { value: 'furnished', label: '家具付き' },
    { value: 'aircon', label: 'エアコン' },
    { value: 'wifi', label: 'Wi-Fi' },
    { value: 'pets', label: 'ペット可' },
    { value: 'parking', label: '駐車場有' },
    { value: 'bicycle', label: '駐輪場有' },
    { value: 'storage', label: '収納' },
    { value: 'autolock', label: 'オートロック' }
];

const sortOptions = [
    { value: 'price_asc', label: '家賃が安い順' },
    { value: 'price_desc', label: '家賃が高い順' },
    { value: 'date_desc', label: '新着順' },
    { value: 'popular', label: '人気順' }
];

const RENT_RANGE = {
    min: 0,
    max: 200000,
    step: 10000
};

const rentMarks = [
    { value: 0, label: '¥0' },
    { value: 50000, label: '¥50,000' },
    { value: 100000, label: '¥100,000' },
    { value: 150000, label: '¥150,000' },
    { value: 200000, label: '¥200,000' }
];

function SearchFilters({
    filters,
    setFilters,
    totalRooms,
    onSearch,
    onClear
}) {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [filterAnchorEl, setFilterAnchorEl] = React.useState(null);
    const [priceAnchorEl, setPriceAnchorEl] = React.useState(null);
    const [urgencyAnchorEl, setUrgencyAnchorEl] = React.useState(null);
    const [searchText, setSearchText] = React.useState(filters.location);

    // デバウンスされた検索処理
    const debouncedSearch = useCallback(
        debounce((value) => {
            setFilters(prev => ({ ...prev, location: value }));
            onSearch();
        }, 500),
        [setFilters, onSearch]
    );

    // 検索テキスト変更時の処理
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchText(value);
        debouncedSearch(value);
    };

    // Enter キー押下時の処理
    const handleSearchKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            debouncedSearch.cancel();
            setFilters(prev => ({ ...prev, location: searchText }));
            onSearch();
            // フォーカスを解除
            e.target.blur();
        }
    };

    // 検索バーのスタイル
    const searchBarStyle = {
        backgroundColor: '#fff',
        borderRadius: '40px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        p: 1,
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        maxWidth: { xs: '100%', md: 600 },
        border: '1px solid #e0e0e0'
    };

    // フィルターチップのスタイルを修正
    const filterChipStyle = {
        borderRadius: '20px',
        height: '36px',
        backgroundColor: 'white',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        '&:hover': {
            backgroundColor: '#f5f5f5'
        },
        // 選択されていない状態のスタイル
        '&.MuiChip-outlined': {
            borderColor: '#e0e0e0',
            color: 'text.secondary',
        },
        // 選択された状態のスタイル
        '&.MuiChip-filled': {
            backgroundColor: 'primary.main',
            color: 'white',
            '&:hover': {
                backgroundColor: 'primary.dark',
            }
        },
        // スマホでの表示を改善
        flex: { xs: '1 1 auto', sm: '0 1 auto' },
        minWidth: { xs: 'calc(50% - 8px)', sm: 'fit-content' }
    };

    // 物件数表示のスタイル
    const totalRoomsChipStyle = {
        backgroundColor: 'rgba(52, 152, 219, 0.08)',
        color: '#3498db',
        fontWeight: 500,
        fontSize: '0.875rem',
        '&:hover': {
            backgroundColor: 'rgba(52, 152, 219, 0.12)'
        }
    };

    // 価格入力欄のスタイル
    const priceInputStyle = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '20px',
            height: '36px',
            backgroundColor: 'white',
            '& fieldset': {
                borderColor: '#e0e0e0',
            },
            '&:hover fieldset': {
                borderColor: '#e0e0e0',
            },
        },
        width: { xs: '100%', sm: 120 }
    };

    const handleSortClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleSortClose = () => {
        setAnchorEl(null);
    };

    const handleSortChange = (value) => {
        setFilters(prev => ({ ...prev, sortBy: value }));
        handleSortClose();
        onSearch();
    };

    const hasActiveFilters = () => {
        return filters.location ||
            filters.propertyType ||
            filters.roomType ||
            filters.availabilityType ||
            filters.selectedAmenities.length > 0 ||
            filters.priceRange[0] !== 0 ||
            filters.priceRange[1] !== 200000;
    };

    const handlePriceChange = (type) => (e) => {
        const value = e.target.value === '' ? '' : Number(e.target.value);
        setFilters(prev => ({
            ...prev,
            priceRange: type === 'min'
                ? [value, prev.priceRange[1]]
                : [prev.priceRange[0], value]
        }));
    };

    const handleRentChange = (event, newValue) => {
        setFilters(prev => ({
            ...prev,
            priceRange: newValue
        }));
    };

    const formatRent = (value) => {
        return `¥${value.toLocaleString()}`;
    };

    return (
        <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {/* メインの検索バー */}
            <Box sx={{
                ...searchBarStyle,
                mx: 'auto'
            }}>
                <TextField
                    fullWidth
                    placeholder="市区町村・路線・駅等で検索できます"
                    value={searchText}
                    onChange={handleSearchChange}
                    onKeyDown={handleSearchKeyDown}
                    variant="standard"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                        disableUnderline: true
                    }}
                    sx={{ px: 2 }}
                />
            </Box>

            {/* フィルターバー - 中央寄せレイアウト */}
            <Box sx={{
                mt: 2,
                width: '100%',
                maxWidth: { xs: '100%', md: 800 },
                mx: 'auto'
            }}>
                <Stack
                    direction="row"
                    flexWrap="wrap"
                    spacing={0}
                    gap={1}
                    sx={{
                        pb: 1,
                        justifyContent: { xs: 'flex-start', md: 'center' }
                    }}
                >
                    {/* 物件数とフィルター */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                            width: '100%',
                            justifyContent: { xs: 'flex-start', md: 'center' },
                            alignItems: 'center'
                        }}
                    >
                        <Box sx={{
                            display: 'flex',
                            gap: 1,
                            flex: { xs: '1 1 100%', sm: '0 0 auto' },
                            mb: { xs: 1, sm: 0 },
                            justifyContent: { xs: 'flex-start', md: 'center' }
                        }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'text.secondary',
                                    fontWeight: 500,
                                    fontSize: '0.875rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5
                                }}
                            >
                                <span style={{ color: '#3498db', fontWeight: 600 }}>{totalRooms}</span>件の物件
                            </Typography>
                            {hasActiveFilters() && (
                                <Chip
                                    label="フィルタークリア"
                                    onDelete={onClear}
                                    deleteIcon={<ClearIcon />}
                                    sx={totalRoomsChipStyle}
                                />
                            )}
                        </Box>

                        <Box sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                            flex: '1 1 auto',
                            justifyContent: { xs: 'flex-start', md: 'center' }
                        }}>
                            <Button
                                variant="outlined"
                                endIcon={<KeyboardArrowDownIcon />}
                                onClick={(e) => setAnchorEl(e.currentTarget)}
                                sx={filterChipStyle}
                            >
                                並び替え
                            </Button>

                            <Button
                                variant="outlined"
                                onClick={(e) => setPriceAnchorEl(e.currentTarget)}
                                endIcon={<KeyboardArrowDownIcon />}
                                sx={{
                                    ...filterChipStyle,
                                    color: filters.priceRange[0] !== 0 || filters.priceRange[1] !== RENT_RANGE.max ? 'primary.main' : 'inherit',
                                    borderColor: filters.priceRange[0] !== 0 || filters.priceRange[1] !== RENT_RANGE.max ? 'primary.main' : '#e0e0e0',
                                }}
                            >
                                家賃 {filters.priceRange[0] !== 0 || filters.priceRange[1] !== RENT_RANGE.max ?
                                    `${formatRent(filters.priceRange[0])} - ${formatRent(filters.priceRange[1])}` :
                                    '指定なし'}
                            </Button>

                            <Button
                                variant="outlined"
                                onClick={(e) => setUrgencyAnchorEl(e.currentTarget)}
                                endIcon={<KeyboardArrowDownIcon />}
                                sx={{
                                    ...filterChipStyle,
                                    color: filters.moveInTiming ? 'primary.main' : 'inherit',
                                    borderColor: filters.moveInTiming ? 'primary.main' : '#e0e0e0',
                                }}
                            >
                                入居可能タイミング
                            </Button>

                            <Button
                                variant="outlined"
                                onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                                startIcon={<FilterListIcon />}
                                sx={{
                                    ...filterChipStyle,
                                    color: (filters.propertyType || filters.roomType || filters.selectedAmenities.length > 0) ? 'primary.main' : 'inherit',
                                    borderColor: (filters.propertyType || filters.roomType || filters.selectedAmenities.length > 0) ? 'primary.main' : '#e0e0e0',
                                }}
                            >
                                詳細条件
                                {(filters.propertyType || filters.roomType || filters.selectedAmenities.length > 0) && (
                                    <Box
                                        component="span"
                                        sx={{
                                            ml: 1,
                                            backgroundColor: 'primary.main',
                                            color: 'white',
                                            borderRadius: '50%',
                                            width: 20,
                                            height: 20,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '0.75rem',
                                        }}
                                    >
                                        {(filters.propertyType ? 1 : 0) +
                                            (filters.roomType ? 1 : 0) +
                                            (filters.selectedAmenities.length > 0 ? 1 : 0)}
                                    </Box>
                                )}
                            </Button>
                        </Box>
                    </Box>
                </Stack>
            </Box>

            {/* ソートメニュー */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
            >
                {sortOptions.map((option) => (
                    <MenuItem
                        key={option.value}
                        onClick={() => {
                            handleSortChange(option.value);
                            setAnchorEl(null);
                        }}
                        selected={filters.sortBy === option.value}
                    >
                        {option.label}
                    </MenuItem>
                ))}
            </Menu>

            {/* 家賃スライダーのポップオーバー */}
            <Popover
                open={Boolean(priceAnchorEl)}
                anchorEl={priceAnchorEl}
                onClose={() => setPriceAnchorEl(null)}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                PaperProps={{
                    sx: {
                        width: { xs: 'calc(100% - 32px)', sm: 400 },
                        maxWidth: { xs: 'calc(100% - 32px)', sm: 400 },
                        m: { xs: 2, sm: 0 }
                    }
                }}
            >
                <Box sx={{ p: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>家賃範囲</Typography>
                    <Box sx={{ px: 2, pt: 3 }}>
                        <Slider
                            value={filters.priceRange}
                            onChange={handleRentChange}
                            valueLabelDisplay="on"
                            valueLabelFormat={formatRent}
                            min={RENT_RANGE.min}
                            max={RENT_RANGE.max}
                            step={RENT_RANGE.step}
                            marks={rentMarks}
                            sx={{
                                '& .MuiSlider-valueLabel': {
                                    backgroundColor: 'primary.main',
                                }
                            }}
                        />
                    </Box>
                    <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
                        <Button onClick={() => {
                            setFilters(prev => ({
                                ...prev,
                                priceRange: [RENT_RANGE.min, RENT_RANGE.max]
                            }));
                            setPriceAnchorEl(null);
                        }}>
                            クリア
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => {
                                onSearch();
                                setPriceAnchorEl(null);
                            }}
                        >
                            適用
                        </Button>
                    </Stack>
                </Box>
            </Popover>

            {/* 詳細条件のポップオーバー */}
            <Popover
                open={Boolean(filterAnchorEl)}
                anchorEl={filterAnchorEl}
                onClose={() => setFilterAnchorEl(null)}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                PaperProps={{
                    sx: {
                        width: { xs: 'calc(100% - 32px)', sm: 400 },
                        maxWidth: { xs: 'calc(100% - 32px)', sm: 400 },
                        m: { xs: 2, sm: 0 }
                    }
                }}
            >
                <Box sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>詳細条件</Typography>

                    {/* 物件タイプ */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" gutterBottom>物件タイプ</Typography>
                        <Stack
                            direction="row"
                            spacing={1}
                            flexWrap="wrap"
                            useFlexGap
                            sx={{ gap: 1 }}
                        >
                            {propertyTypes.map((type) => (
                                <Chip
                                    key={type.value}
                                    label={type.label}
                                    onClick={() => setFilters(prev => ({
                                        ...prev,
                                        propertyType: prev.propertyType === type.value ? '' : type.value
                                    }))}
                                    variant={filters.propertyType === type.value ? "filled" : "outlined"}
                                    sx={{
                                        ...filterChipStyle,
                                        flex: { xs: '1 1 calc(50% - 8px)', sm: '0 1 auto' }
                                    }}
                                />
                            ))}
                        </Stack>
                    </Box>

                    {/* 部屋タイプ */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" gutterBottom>部屋タイプ</Typography>
                        <Stack
                            direction="row"
                            spacing={1}
                            flexWrap="wrap"
                            useFlexGap
                            sx={{ gap: 1 }}
                        >
                            {roomTypes.map((type) => (
                                <Chip
                                    key={type.value}
                                    label={type.label}
                                    onClick={() => setFilters(prev => ({
                                        ...prev,
                                        roomType: prev.roomType === type.value ? '' : type.value
                                    }))}
                                    variant={filters.roomType === type.value ? "filled" : "outlined"}
                                    sx={{
                                        ...filterChipStyle,
                                        flex: { xs: '1 1 calc(50% - 8px)', sm: '0 1 auto' }
                                    }}
                                />
                            ))}
                        </Stack>
                    </Box>

                    {/* 居住可能期間 */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" gutterBottom>居住可能期間</Typography>
                        <Stack
                            direction="row"
                            spacing={1}
                            flexWrap="wrap"
                            useFlexGap
                            sx={{ gap: 1 }}
                        >
                            {availabilityType.map((type) => (
                                <Chip
                                    key={type.value}
                                    label={type.label}
                                    onClick={() => setFilters(prev => ({
                                        ...prev,
                                        availabilityType: prev.availabilityType === type.value ? '' : type.value
                                    }))}
                                    variant={filters.availabilityType === type.value ? "filled" : "outlined"}
                                    sx={{
                                        ...filterChipStyle,
                                        flex: { xs: '1 1 calc(50% - 8px)', sm: '0 1 auto' }
                                    }}
                                />
                            ))}
                        </Stack>
                    </Box>

                    {/* 設備・アメニティ */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" gutterBottom>設備・アメニティ</Typography>
                        <Stack
                            direction="row"
                            spacing={1}
                            flexWrap="wrap"
                            useFlexGap
                            sx={{ gap: 1 }}
                        >
                            {amenities.map((amenity) => (
                                <Chip
                                    key={amenity.value}
                                    label={amenity.label}
                                    onClick={() => {
                                        setFilters(prev => ({
                                            ...prev,
                                            selectedAmenities: prev.selectedAmenities.includes(amenity.value)
                                                ? prev.selectedAmenities.filter(a => a !== amenity.value)
                                                : [...prev.selectedAmenities, amenity.value]
                                        }));
                                    }}
                                    variant={filters.selectedAmenities.includes(amenity.value) ? "filled" : "outlined"}
                                    sx={{
                                        ...filterChipStyle,
                                        flex: { xs: '1 1 calc(50% - 8px)', sm: '0 1 auto' }
                                    }}
                                />
                            ))}
                        </Stack>
                    </Box>

                    <Stack direction="row" justifyContent="space-between" sx={{ mt: 3 }}>
                        <Button
                            onClick={() => {
                                setFilters(prev => ({
                                    ...prev,
                                    propertyType: '',
                                    roomType: '',
                                    availabilityType: '',
                                    selectedAmenities: []
                                }));
                                setFilterAnchorEl(null);
                            }}
                        >
                            クリア
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => {
                                onSearch();
                                setFilterAnchorEl(null);
                            }}
                        >
                            条件を適用
                        </Button>
                    </Stack>
                </Box>
            </Popover>

            {/* 入居可能タイミングポップオーバー */}
            <Popover
                open={Boolean(urgencyAnchorEl)}
                anchorEl={urgencyAnchorEl}
                onClose={() => setUrgencyAnchorEl(null)}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                PaperProps={{
                    sx: {
                        width: { xs: 'calc(100% - 32px)', sm: 300 },
                        maxWidth: { xs: 'calc(100% - 32px)', sm: 300 },
                        m: { xs: 2, sm: 0 }
                    }
                }}
            >
                <Box sx={{ p: 3, width: 300 }}>
                    <Typography variant="subtitle1" gutterBottom>入居可能タイミング</Typography>
                    <Stack spacing={1}>
                        {moveinTypes.map((type) => (
                            <Chip
                                key={type.value}
                                label={type.label}
                                onClick={() => {
                                    setFilters(prev => ({
                                        ...prev,
                                        moveInTiming: prev.moveInTiming === type.value ? '' : type.value
                                    }));
                                }}
                                variant={filters.moveInTiming === type.value ? "filled" : "outlined"}
                                sx={filterChipStyle}
                            />
                        ))}
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" sx={{ mt: 3 }}>
                        <Button
                            onClick={() => {
                                setFilters(prev => ({
                                    ...prev,
                                    moveInTiming: ''
                                }));
                                setUrgencyAnchorEl(null);
                            }}
                        >
                            クリア
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => {
                                onSearch();
                                setUrgencyAnchorEl(null);
                            }}
                        >
                            条件を適用
                        </Button>
                    </Stack>
                </Box>
            </Popover>
        </Box>
    );
}

export default SearchFilters;